import React from 'react';
import moment from 'moment';
import { useSetGoalContext } from '../../context/useSetGoalContext';
import { StepCard } from '../../../PulseCheck.styles';
import { cardAnimationVariants } from '../../../../BeFi20/BeFi20.meta';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { getGoalSteps } from '../../utils/getGoalSteps';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { IconButton } from '../../../../CommonComponents/OPL/IconButton/IconButton';
import { FinancialGoalType } from '../../context/SetGoalProvider';
import { EXPENSE_GOAL_LABEL, LARGE_PURCHASE_GOAL_LABEL } from '../GoalType/GoalType.meta';
import { formatCurrency, formatNumber } from '../../../../CommonUtils/formatNumber';
import { FORM_ID, StepName } from '../../SetGoal.meta';
import { ReviewRowWrapper } from './Review.styles';
import { getDisplayDate } from '../../utils/getDisplayDate';
import { getExpenseStartDate } from '../../utils/getExpenseStartDate';
import { StepWrapper } from '../../../components/StepWrapper/StepWrapper';

export const Review = () => {
  const { wizard, onSuccess, setStep, setIsReviewingStep } = useSetGoalContext();

  const onSubmit = (evt: React.FormEvent) => {
    evt.preventDefault();

    onSuccess(wizard);
  };

  const onStepEdit = (step: StepName) => {
    setIsReviewingStep(true);
    setStep(step);
  };

  const steps = getGoalSteps(wizard);

  const accountsCount = (wizard?.accountIds || []).length;

  return (
    <StepWrapper as="form" id={FORM_ID} onSubmit={onSubmit}>
      <StepCard
        variants={cardAnimationVariants}
        initial="enter"
        animate="visible"
        style={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}
      >
        <Text as="h1" fontSize="h4" fontWeight={600} mb="16" px="32">
          Please review the information
        </Text>

        <>
          {steps.map(step => (
            <React.Fragment key={step}>
              {step === 'goalDescription' && (
                <ReviewRow
                  label="Name"
                  value={wizard.goalDescription || ''}
                  onClick={() => onStepEdit(step)}
                />
              )}

              {step === 'goalType' && (
                <ReviewRow
                  label="Best supports your goal"
                  value={
                    wizard.goalType === FinancialGoalType.LargePurchase
                      ? LARGE_PURCHASE_GOAL_LABEL
                      : EXPENSE_GOAL_LABEL
                  }
                  onClick={() => onStepEdit(step)}
                />
              )}

              {step === 'budget' && wizard.goalType === FinancialGoalType.LargePurchase && (
                <>
                  <ReviewRow
                    label="Budget"
                    value={`${formatCurrency(wizard.purchase?.amount || 0, {
                      mantissa: 2,
                      optionalMantissa: true,
                    })} / ${wizard.purchase?.isRecurringPurchase ? 'recurring' : 'one-time'}`}
                    onClick={() => onStepEdit(step)}
                  />

                  <ReviewRow
                    label="Inflation"
                    value={`${formatNumber(wizard.purchase?.inflation || 0, {
                      mantissa: 2,
                      optionalMantissa: true,
                    })}%`}
                    onClick={() => onStepEdit(step)}
                  />

                  {wizard.purchase && !wizard.purchase.isRecurringPurchase && (
                    <ReviewRow
                      label="Target date"
                      value={getDisplayDate(wizard.purchase.oneTimeOptions?.targetYear)}
                      onClick={() => onStepEdit(step)}
                    />
                  )}

                  {wizard.purchase && wizard.purchase.isRecurringPurchase && (
                    <ReviewRow
                      label="Start date"
                      value={getDisplayDate(wizard.purchase.recurringOptions?.startYear)}
                      onClick={() => onStepEdit(step)}
                    />
                  )}
                </>
              )}

              {step === 'budget' && wizard.goalType === FinancialGoalType.Expense && (
                <>
                  <ReviewRow
                    label="Budget"
                    value={`${formatCurrency(wizard.expense?.amount || 0, {
                      mantissa: 2,
                      optionalMantissa: true,
                    })} / recurring`}
                    onClick={() => onStepEdit(step)}
                  />

                  <ReviewRow
                    label="Start date"
                    value={getExpenseStartDate(wizard.expense?.startDate)}
                    onClick={() => onStepEdit(step)}
                  />
                </>
              )}

              {step === 'accounts' && (
                <ReviewRow
                  label="Account(s)"
                  value={
                    accountsCount > 0
                      ? `${accountsCount} Account${accountsCount > 1 ? 's' : ''}`
                      : '--'
                  }
                  onClick={() => onStepEdit(step)}
                />
              )}

              {step === 'targetDate' && (
                <ReviewRow
                  label="Target date"
                  value={moment(wizard.targetDate).format('MM/DD/YYYY')}
                  onClick={() => onStepEdit(step)}
                />
              )}
            </React.Fragment>
          ))}
        </>
      </StepCard>
    </StepWrapper>
  );
};

interface ReviewRowProps {
  label: string;
  value: string;
  onClick(): void;
}

const ReviewRow = ({ label, value, onClick }: ReviewRowProps) => {
  return (
    <ReviewRowWrapper>
      <Box flex={1} pr="16">
        <Text fontWeight={500} color="neutral.500">
          {label}
        </Text>
      </Box>

      <Box flex={1} pr="16">
        <Text fontWeight={500}>{value}</Text>
      </Box>

      <Box flexShrink={0}>
        <IconButton
          icon="far fa-pencil"
          variant="subtleInactive"
          onClick={onClick}
          aria-label={`Edit ${label}`}
        />
      </Box>
    </ReviewRowWrapper>
  );
};
